import { ref } from 'vue';

import { useAuthStore } from '@/platform/auth-runtime/store';
import { STORAGE_KEYS } from '@/platform/auth-runtime/token-storage';
import { ACCOUNT_INFO_KEY, accountInfoQueryOptions } from '@/platform/identity/account-info-query';
import { identityApi } from '@/platform/identity/api';
import { queryClient } from '@/shared/lib/query-client';
import { SourceFrom } from '@/shared/source';
import { defineStore } from 'pinia';

import type { UserInfoResponse } from '@/platform/identity/api';

// Identity store holds session-derived identity state: user / perms /
// dataPerms. accountInfo is server state and lives in vue-query — see
// platform/identity/queries.ts. Consumers needing reactive accountInfo
// call useAccountInfoQuery(); sync snapshots read via
// queryClient.getQueryData(ACCOUNT_INFO_KEY).

// Local type patch instead of modifying cloud-typings (which is generator-
// owned). Legacy reads `userInfo.loginMethod` (SAML/OIDC routing),
// `userInfo.role` (frontend-alert tag) and `userInfo.accountName` (MCP consent
// card) at runtime today — backend returns them but PbAccount.User doesn't
// list them.
export type EnrichedUserInfo = UserInfoResponse & {
  isMarketPlace?: boolean;
  loginMethod?: string;
  role?: string;
  accountName?: string;
  // True when the account's SSO config maps IdP groups to roles, i.e. the
  // identity provider owns role assignment and the console must not offer it.
  hasSSORoleMapping?: boolean;
};

export const useUserStore = defineStore('user', () => {
  const info = ref<EnrichedUserInfo | undefined>(undefined);
  const perms = ref<string[]>([]);
  const dataPerms = ref<Record<string, string[]>>({});
  const permsTimestamp = ref(0);

  // clearOnFail wipes auth + identity on failure — right on boot (a stale ubid
  // must bounce to /login), wrong on login (a transient blip must not nuke the
  // just-issued token), so the login caller passes false.
  async function fetchInfo({ clearOnFail = true }: { clearOnFail?: boolean } = {}): Promise<
    EnrichedUserInfo | undefined
  > {
    const ubid = localStorage.getItem(STORAGE_KEYS.ubid);
    if (!ubid) return undefined;

    try {
      const res = await identityApi['/account/user-info']({ bizID: ubid });
      if (!res) return undefined;

      if (res.userID && res.userID !== ubid) {
        const { reportManualError } = await import('@/platform/telemetry/frontend-alert');
        reportManualError('request_interceptor_error', 'x-sr-ubid not match userInfo.userID', {
          responseData: res,
          requestData: { bizID: ubid },
          userBrowserId: localStorage.getItem(STORAGE_KEYS.ubid),
        });
      }

      const isMarketPlace =
        !!res.marketplaceCustomerId ||
        res.sourceFrom === SourceFrom.AwsMarketplace ||
        res.sourceFrom === SourceFrom.AzureMarketplace;
      const enriched: EnrichedUserInfo = { ...res, isMarketPlace };

      // Pre-load accountInfo into vue-query cache before committing identity
      // state. LegacyShellHost.getInitialState reads ACCOUNT_INFO_KEY at sub
      // module-eval to seed sub's mirror + drain it into legacy Vuex; without
      // this await, the seed lands with accountInfo:null and sub renders
      // /support paid=false until its own getAccountInfo settles (the
      // community → premium flash).
      if (enriched.accountID) {
        await queryClient
          .ensureQueryData(accountInfoQueryOptions(enriched.accountID))
          .catch(() => undefined);
      }

      info.value = enriched;
      perms.value = enriched.perms ?? [];
      dataPerms.value = enriched.data_perms ?? {};
      permsTimestamp.value = Date.now();

      return enriched;
    } catch {
      // Bootstrap failed (blip / 5xx; 50001 goes through the axios auth gate).
      // On boot, clearing bounces the routing guard to /login so no protected
      // route mounts with a dead session behind the loading mask.
      if (clearOnFail) {
        useAuthStore().clearAuth();
        clear();
      }
      return undefined;
    }
  }

  async function fetchPerms(): Promise<void> {
    const res = await identityApi['/account/user-perms']();
    perms.value = res.perms ?? [];
    dataPerms.value = res.dataPerms ?? {};
    permsTimestamp.value = Date.now();
  }

  function clear(): void {
    info.value = undefined;
    perms.value = [];
    dataPerms.value = {};
    permsTimestamp.value = 0;
    queryClient.removeQueries({ queryKey: ACCOUNT_INFO_KEY });
  }

  return {
    info,
    perms,
    dataPerms,
    permsTimestamp,
    fetchInfo,
    fetchPerms,
    clear,
  };
});
